import { useMemo, useState } from 'react';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import {
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ResponsiveContainer,
  TooltipContentProps,
} from 'recharts';
import { Button } from '@/components/ui/button.tsx';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import {
  useGetChartTradingQuery,
  JobStatusInfo,
} from '../hooks/queries/use-get-chart-trading';
import { format, isToday, isTomorrow, Locale } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { enUS, ro } from 'date-fns/locale';
import type {
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

type ChartDataPoint = {
  time: string;
  forecast?: number;
  actualLoad?: number;
};

const METRIC_COLORS = {
  FORECAST_LINE: '#3b82f6',
  ACTUAL_LOAD_LINE: '#22c55e',
} as const;

const METRIC_KEYS = {
  FORECAST_LINE: 'forecast_line',
  ACTUAL_LOAD_LINE: 'actual_load_line',
} as const;

const getStatusBadgeColor = (status: JobStatusInfo): string => {
  switch (status) {
    case 'ready':
      return 'bg-green-100 text-green-800';
    case 'processing':
      return 'bg-blue-100 text-blue-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'partial':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusLabel = (status: JobStatusInfo): string => {
  switch (status) {
    case 'ready':
      return 'ENTSO-E Ready';
    case 'processing':
      return 'ENTSO-E Processing';
    case 'failed':
      return 'ENTSO-E Failed';
    case 'partial':
      return 'ENTSO-E Partial';
    default:
      return 'ENTSO-E Unknown';
  }
};

const CHART_METRICS = [
  {
    id: METRIC_KEYS.FORECAST_LINE,
    label: 'Forecast',
    color: METRIC_COLORS.FORECAST_LINE,
    selected: true,
  },
  {
    id: METRIC_KEYS.ACTUAL_LOAD_LINE,
    label: 'Actual Load',
    color: METRIC_COLORS.ACTUAL_LOAD_LINE,
    selected: true,
  },
] as const;

export const LoadForecastComparison = ({ selectedDate }: { selectedDate: Date }) => {
  const { i18n } = useTranslation(['translation'], { keyPrefix: 'metrics' });
  const { t } = useTranslation();
  const locale = (i18n.language as 'ro' | 'en') === 'ro' ? ro : enUS;

  const { data: queryResult, isLoading } = useGetChartTradingQuery({
    date: selectedDate,
    type: 'trading-opportunity-timeline',
  });

  const chartData = queryResult?.data;
  const status = queryResult?.status;

  const isMobile = useMediaQuery('(max-width:640px)');
  const [selectedState, setSelectedState] = useState<Record<string, boolean>>({});

  const fields = useMemo(
    () =>
      CHART_METRICS.map((metric) => ({
        ...metric,
        selected: selectedState[metric.id] ?? true,
      })),
    [selectedState]
  );

  const data: ChartDataPoint[] = useMemo(() => {
    if (!chartData) return [];

    const forecastData = Array.isArray(chartData.forecast) ? chartData.forecast : [];
    const actualLoadData = Array.isArray(chartData.actualLoad) ? chartData.actualLoad : [];

    const timeMap = new Map<string, ChartDataPoint>();

    forecastData.forEach((point) => {
      timeMap.set(point.time, {
        time: point.time,
        forecast: point.value,
      });
    });

    actualLoadData.forEach((point) => {
      const existing = timeMap.get(point.time);
      if (existing) {
        existing.actualLoad = point.value;
      } else {
        timeMap.set(point.time, {
          time: point.time,
          actualLoad: point.value,
        });
      }
    });

    return Array.from(timeMap.values()).sort((a, b) => a.time.localeCompare(b.time));
  }, [chartData]);

  const hasNoData =
    !chartData ||
    ((!chartData.forecast || chartData.forecast.length === 0) &&
      (!chartData.actualLoad || chartData.actualLoad.length === 0));

  return (
    <DashboardCard isLoading={isLoading} className="min-h-[460px]">
      <div className="mb-4 flex items-center justify-between text-base font-semibold">
        <span>Load Forecast vs Actual</span>
        {status && (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeColor(status)}`}
          >
            {getStatusLabel(status)}
          </span>
        )}
      </div>
      {hasNoData ? (
        <div className="flex h-full min-h-[238px] w-full items-center justify-center rounded-md bg-gray-50">
          <span className="text-muted-foreground">
            {t('devices.batteryModule.noDataFound')}
          </span>
        </div>
      ) : (
        <div className="flex w-full flex-col gap-2 select-none">
          <div className={isMobile ? 'h-[360px] w-full' : 'h-[420px] w-full'}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data}
                margin={{
                  top: 8,
                  right: isMobile ? 0 : 8,
                  bottom: 8,
                  left: isMobile ? -10 : 0,
                }}
              >
                <CartesianGrid vertical={false} stroke="#e5e7eb" />
                <XAxis
                  key="xAxis-main"
                  dataKey="time"
                  minTickGap={40}
                  interval={7}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: isMobile ? 12 : 16 }}
                  tickFormatter={(t) => {
                    return formatDate(t, { days: 1, weeks: 0, months: 0, years: 0 }, locale);
                  }}
                />
                <YAxis tick={{ fontSize: isMobile ? 12 : 16 }} />
                <Tooltip
                  cursor={false}
                  content={(props) => (
                    <ChartTooltipComponent
                      {...props}
                      dateDifference={{ days: 1, weeks: 0, months: 0, years: 0 }}
                      locale={locale}
                    />
                  )}
                />

                {fields.find((f) => f.id === METRIC_KEYS.FORECAST_LINE)?.selected && (
                  <Line
                    type="monotone"
                    dataKey="forecast"
                    stroke={METRIC_COLORS.FORECAST_LINE}
                    strokeWidth={2}
                    dot={false}
                    name="Forecast"
                  />
                )}
                {fields.find((f) => f.id === METRIC_KEYS.ACTUAL_LOAD_LINE)?.selected && (
                  <Line
                    type="monotone"
                    dataKey="actualLoad"
                    stroke={METRIC_COLORS.ACTUAL_LOAD_LINE}
                    strokeWidth={2}
                    dot={false}
                    name="Actual Load"
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="mx-auto flex flex-wrap gap-1 md:gap-1">
            {fields.map((f) => (
              <Button
                key={f.id}
                variant="ghost"
                style={{ opacity: !f.selected ? '0.4' : '1' }}
                className="h-min w-min justify-start p-1 px-2"
                onClick={() =>
                  setSelectedState((current) => ({
                    ...current,
                    [f.id]: !f.selected,
                  }))
                }
              >
                <div className="flex items-center gap-2">
                  <span className="size-4 rounded-full" style={{ backgroundColor: f.color }} />
                  <span className="text-start text-sm font-medium text-nowrap text-[#7D7D85]">
                    {f.label}
                  </span>
                </div>
              </Button>
            ))}
          </div>
        </div>
      )}
    </DashboardCard>
  );
};

export default LoadForecastComparison;

const ChartTooltipComponent = ({
  payload,
  label,
  dateDifference,
  locale,
}: TooltipContentProps<ValueType, NameType> & {
  dateDifference: {
    days: number;
    weeks: number;
    months: number;
    years: number;
  };
  locale: Locale;
}) => {
  const formattedDate = label ? formatDate(new Date(label), dateDifference, locale) : '';

  const formatValue = (p: Payload<ValueType, NameType>): string => {
    if (p?.dataKey && p.dataKey === 'battery_state_of_charge') return `${p.value} %`;

    if (!p?.value) return '0 kWh';

    const value = Number(p.value);
    const fixedValue = +value.toFixed(2);
    const isBatteryPower = p.name === 'Battery Power';

    if (value < 1000) {
      return isBatteryPower ? `${fixedValue} kWh` : `${Math.abs(fixedValue)} kWh`;
    }

    const megaWattValue = (value / 1000).toFixed(2);
    return isBatteryPower ? `${megaWattValue} MWh` : `${Math.abs(Number(megaWattValue))} MWh`;
  };

  return (
    <div className="flex flex-col border border-gray-300 bg-white p-4 text-base">
      <p>{formattedDate}</p>
      <div className="mt-2">
        {payload?.map((p: Payload<ValueType, NameType>) => (
          <p style={{ color: p.stroke }} key={p.name}>
            {p.name} : {formatValue(p)}
          </p>
        ))}
      </div>
    </div>
  );
};

function formatDate(
  date: Date,
  dateDifference?: {
    days: number;
    weeks: number;
    months: number;
    years: number;
  },
  locale?: Locale
): string {
  if (!dateDifference) return '';

  const capitalizeWords = (str: string) =>
    str
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const { days } = dateDifference;

  if ((isToday(date) && days <= 1) || (isTomorrow(date) && days <= 1) || days <= 1) {
    return format(date, 'HH:mm');
  } else {
    return capitalizeWords(format(date, 'MMM d, HH:mm', { locale }));
  }
}
